import React, { useState, useEffect } from 'react';
import { circuitsApi } from '../services/api';
import { Circuit, CircuitCreate } from '../types';

const CircuitsPage: React.FC = () => {
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCircuit, setSelectedCircuit] = useState<Circuit | null>(null);

  useEffect(() => {
    loadCircuits();
  }, []);

  const loadCircuits = async () => {
    try {
      setLoading(true);
      const response = await circuitsApi.getAll();
      setCircuits(response.data);
    } catch (error) {
      console.error('Failed to load circuits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCircuit = async (circuitData: CircuitCreate) => {
    try {
      const response = await circuitsApi.create(circuitData);
      setCircuits([response.data, ...circuits]);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create circuit:', error);
      alert('Failed to create circuit. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading circuits...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Circuits</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Circuit
          </button>
        </div>

        {showCreateForm && (
          <CreateCircuitForm
            onSubmit={handleCreateCircuit}
            onCancel={() => setShowCreateForm(false)}
          />
        )}

        {selectedCircuit && (
          <CircuitDetails
            circuit={selectedCircuit}
            onClose={() => setSelectedCircuit(null)}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {circuits.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedCircuit(c)}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{c.name}</h3>
              <p className="text-gray-600 mb-2">{c.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Slug: {c.slug}</span>
                <span className="text-sm text-gray-500">
                  Created: {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
              {c.start_date && (
                <div className="mt-2 text-sm text-gray-500">
                  Start: {new Date(c.start_date).toLocaleDateString()}
                </div>
              )}
              {c.end_date && (
                <div className="text-sm text-gray-500">
                  End: {new Date(c.end_date).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>

        {circuits.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No circuits found. Create your first circuit!</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface CreateCircuitFormProps {
  onSubmit: (circuit: CircuitCreate) => void;
  onCancel: () => void;
}

const CreateCircuitForm: React.FC<CreateCircuitFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    start_date: '',
    end_date: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      alert('Name and slug are required');
      return;
    }
    onSubmit(formData);
  };

  const handleSlugChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setFormData({ ...formData, name, slug });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Create Circuit</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Circuit Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleSlugChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Holland Budget Circuit"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slug *
          </label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., holland-budget-circuit"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            URL-friendly identifier (auto-generated from name)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Optional description of the circuit..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Circuit
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

interface CircuitDetailsProps {
  circuit: Circuit;
  onClose: () => void;
}

const CircuitDetails: React.FC<CircuitDetailsProps> = ({ circuit, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{circuit.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Slug</label>
            <p className="text-gray-900">{circuit.slug}</p>
          </div>

          {circuit.description && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <p className="text-gray-900">{circuit.description}</p>
            </div>
          )}

          {circuit.start_date && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <p className="text-gray-900">{new Date(circuit.start_date).toLocaleDateString()}</p>
            </div>
          )}

          {circuit.end_date && (
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <p className="text-gray-900">{new Date(circuit.end_date).toLocaleDateString()}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Created</label>
            <p className="text-gray-900">{new Date(circuit.created_at).toLocaleString()}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Last Updated</label>
            <p className="text-gray-900">{new Date(circuit.updated_at).toLocaleString()}</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CircuitsPage;
